import { AfterViewInit, Component, Inject, LOCALE_ID, ViewChild } from '@angular/core';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ConfirmFeatureSetField, DateFeatureSetField, FeatureSetField } from '../backend-types/feature-set';
import { ApiAboutService } from '../Services/api-about.service';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Router } from '@angular/router';
import { VerifyForm } from '../Services/session.service';
import { ApiService, verifyWithOrgAPI } from '../api/api.service';
import { DialogService, DialogType } from '../Services/dialog.service';
import { ElectorDataService } from '../Services/elector-data.service';
import { email } from '../verify-voter/verify-voter.component';
import { CustomValidators } from '../custom-validators/custom-validators';
import { SearchCountryField } from '@justin-s/ngx-intl-tel-input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxIntlTelInputModule } from '@justin-s/ngx-intl-tel-input';
import { HeaderModule } from "../header/header.module";
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { SharedModule } from '../shared/shared.module';
import { dashboardRoute } from '../route-names';
import { ElectorAndBallot } from '../ballot/backend-types';
import { BallotListService } from '../ballot-list/ballot-list.service';
import { BackButtonConfirmationModalModule } from '../back-button-confirmation-modal/back-button-confirmation-modal.module';

export const maxEmailLength = 254;   // From RFC 3696, erratum 1690.
export const maxPINLength = 16;

@Component({
    selector: 'app-verify-with-org',
    imports: [MatProgressSpinnerModule, NgxIntlTelInputModule, HeaderModule, ReactiveFormsModule,
        MatTooltipModule, CommonModule, MatIconModule, SharedModule, BackButtonConfirmationModalModule
    ],
    templateUrl: './verify-with-org.component.html',
    styleUrl: './verify-with-org.component.scss'
})

export class VerifyWithOrgComponent implements AfterViewInit{

  @ViewChild(DatePickerComponent) datePickerComponent!: DatePickerComponent;

  verificationForm: FormGroup = new FormGroup({});
  fields: Array<FeatureSetField | ConfirmFeatureSetField | DateFeatureSetField> = [];

  dateField?: DateFeatureSetField;
  defaultDateParts: Set<"day" | "month" | "year"> = new Set(["day", "month", "year"]);
  removedDateFields: FeatureSetField[] = [];

  submitted = false;
  country: string;
  SearchCountryField = SearchCountryField;
  readonly maxEmailLength: number = maxEmailLength;
  readonly maxPINLength: number = maxPINLength;
  displaySpinner = false;
  dialogTitle?: string;
  dialogMessage?: string;
  hide = true;
  
  constructor(
    readonly aboutService: ApiAboutService,
    private readonly fb: FormBuilder,
    public readonly announcer: LiveAnnouncer,
    @Inject(LOCALE_ID) readonly locale: string,
    private readonly router: Router,
    private readonly apiService: ApiService,
    private dialogService: DialogService,
    private readonly dialog: MatDialog,
    readonly userService: ElectorDataService,
    private readonly ballotListService: BallotListService
  ) {
    this.country = this.aboutService.aboutInformation.value.country;
    this.fields = this.aboutService.dashboardFlowFeatureSet.featureSetFields.fields.filter((field) => field.isMandatory);
    this.processFeatureSetFields(this.fields)

    this.verificationForm = new FormGroup({})
    this.addFormValidators();
  }

  announce(text: string) {
    this.announcer.announce(text, "polite");
  }

  //Fix for WAVE error *************************************************
  ngAfterViewInit() {
    if(!this.userService.isOrgVerificationAllowed){
      this.dialogMessage = $localize`You have no organization verification attempts remaining.<br><br>You may attempt to verify with an organization again in ${this.userService.orgVerifyLockoutTimeRemainingString()}.`;
      this.dialogTitle = $localize`Please Wait.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
      this.router.navigate(dashboardRoute);
      return;
    }

    this.announcer.announce($localize`Identity Verfication`, "assertive")
    if (document.readyState === "complete") {
      setTimeout(() => {
        this.resetFocus()
      }, 200);
    }

    const isDateFieldPresent = this.fields.find((field) => field.fieldType === 'date')
    if (isDateFieldPresent) {
      this.verificationForm.addControl(isDateFieldPresent.fieldName, this.datePickerComponent.formGroup);
      this.datePickerComponent.formGroup.setParent(this.verificationForm);
    }
  }
  /*********************************************************************/
  resetFocus() {
    let focusField = document.getElementById('accessibility-button')
    let focusFieldMobile = document.getElementById('accessibility-button-mobile')

    if (focusField) {
      focusField.focus()
    }

    if (focusFieldMobile) {
      focusFieldMobile.focus()
    }
  }

  onTooltipClick(event: any) {
    event.preventDefault();
  }

  onSubmit() {
    this.submitted = true;
    if(!this.userService.isOrgVerificationAllowed){
      this.dialogMessage = $localize`You have no attempts remaining.<br><br>You may attempt to verify with an organization again in ${this.userService.orgVerifyLockoutTimeRemainingString()}.`;
      this.dialogTitle = $localize`Please Wait.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
      this.router.navigate(dashboardRoute);
      return;
    } else if (!this.verificationForm.valid) {
      this.announcer.announce($localize`error Some fields are missing or invalid.`, "assertive");
    } else {
      this.displaySpinner = true
      const form: VerifyForm = { keyPairs: [] }
      const keys = Object.keys(this.verificationForm.controls)
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        let value: string = this.verificationForm.get(key)?.value as string
        const field = this.fields.find((f) => f.fieldName === key)
        if ((field) && (field.fieldType === 'phone')) {
          if(this.verificationForm.get(key)?.value === null) {
            continue
          }
          value = this.verificationForm.get(key)?.value.e164Number;
        }
        if ((field) && (field.fieldType === 'date')) {
          if (this.removedDateFields.length > 0) {
            const dateTime = this.verificationForm.get(key)?.value
            this.removedDateFields.forEach((removed) => {
              switch (removed.fieldType) {
                case 'day':
                  value = String(dateTime['day']).padStart(2, '0')
                  break
                case 'month':
                  value = String(dateTime['month']).padStart(2, '0')
                  break
                case 'year':
                  value = String(dateTime['year']).padStart(4, '0')
                  break
              }
              const keyPair = { key: removed.fieldName, value: value }
              form.keyPairs.push(keyPair)
            })
            continue
          } else {
            let dateTime = this.verificationForm.get(key)?.value
            let dateString = String(dateTime['year']).padStart(4, '0') + '-' + String(dateTime['month']).padStart(2, '0') + '-' + String(dateTime['day']).padStart(2, '0')
            value = dateString
          }
        }
        const keyPair = { key: key, value: value }
        form.keyPairs.push(keyPair)
      }
      this.verifyWithOrg(form);
    }
  }

  verifyWithOrg(form: VerifyForm) {
    const requestBody = {
      data: form.keyPairs,
      orgId: this.aboutService.dashboardFlowFeatureSet.orgId
    };
     this.apiService.postWithHandshakeCheck<ElectorAndBallot>(verifyWithOrgAPI, requestBody).subscribe({
      next: (response) => {
        this.displaySpinner = false;
        if(response.body?.orgVerifications) {
          let sortedOrgs = response.body.orgVerifications?.sort((a, b) => (new Date(b.oidvTimestamp ?? 0).getTime() - new Date(a.oidvTimestamp ?? 0).getTime()));
          if(sortedOrgs && sortedOrgs.length > 0) {
            let mostRecentOrgVerification = sortedOrgs[0];
            this.userService.addOrgVerification(mostRecentOrgVerification);

            if(response.body.elector?.idv_status && response.body.elector.idv_status_timestamp) {
              this.userService.idvStatus.set(response.body.elector.idv_status);
              this.userService.idvTimestamp.set(response.body.elector.idv_status_timestamp);
            }
            this.ballotListService.setBallots(response.body);

            this.dialogTitle = $localize`Success!`;
            this.dialogMessage = $localize`You have been successfully verified by ${mostRecentOrgVerification.organizationName}.`;
            this.dialogService.openDialog(DialogType.SUCCESS, this.dialogTitle, this.dialogMessage);
        
            setTimeout(() => {
              this.dialog.closeAll();
              this.router.navigate(dashboardRoute);
            }, 2000);
          }
        } else {
          this.dialogMessage = $localize`One or more fields contains an error.Please verify your information and try again. If you believe this is in error, use the <b>"Contact Support"</b> link for assistance.`;
          this.dialogTitle = $localize`Incorrect Information:`;
          this.dialogService.openDialog( DialogType.ERROR, this.dialogTitle, this.dialogMessage );
        }
      },
      error: (err) => {
        this.displaySpinner = false;
        if ((err.error_type !== undefined && err.error_type === 'INVALID_IDENTITY') ||
          (err.error !== undefined && err.error.error_type === 'INVALID_IDENTITY')) {
          this.dialogTitle = $localize`Incorrect Information:`;
          this.dialogMessage = $localize`One or more fields contains an error.Please verify your information and try again. If you believe this is in error, use the <b>"Contact Support"</b> link for assistance.`;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
        }  else if ((err.error_type !== undefined && (err.error_type === 'DECODER_STATE_LOST' || err.error_type === 'INVALID_MTE_DECODING')) ||
        (err.error !== undefined && (err.error.error_type === 'DECODER_STATE_LOST' || err.error.error_type === 'INVALID_MTE_DECODING' ))) {
          this.dialogMessage = $localize`We could not process your request. Please log out, log in again, and try again.`;
          this.dialogTitle = $localize`Error`
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
        } else if ((err.error_type !== undefined && err.error_type === 'INVALID_API') ||
        (err.error !== undefined && err.error.error_type === 'INVALID_API')) {
          this.dialogMessage = $localize`The API key is invalid. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Invalid API Key`;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
        } else  if ((err.error_type !== undefined && err.error_type === 'ALREADY_REGISTERED') ||
          (err.error !== undefined && err.error.error_type === 'ALREADY_REGISTERED')) {
          this.dialogTitle = $localize`Already verified`
          this.dialogMessage = $localize`The supplied verification data has already been used by another user to be verified by this organization. If you believe this is in error, use the <b>"Contact Support"</b> link for assistance.`;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
        } else if ((err.error_type !== undefined && err.error_type === 'INVALID_FORM') ||
        (err.error !== undefined && err.error.error_type === 'INVALID_FORM')) {
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Malformed Data`
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
        } else if ((err.error_type !== undefined && err.error_type === 'VERIFY_ORG_LOCKED') ||
        (err.error !== undefined && err.error.error_type === 'VERIFY_ORG_LOCKED')) {
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.userService.orgVerifyAttemptsRemaining = 1;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogTitle = $localize`Too Many Attempts`;
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
        } else {
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.dialogTitle = $localize`Unknown Error`;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
        }
      }
    });

  }

  focusErrorDialog() {
    setTimeout(() => {
      const focusableElements = 'button';
      const modal: any = document.querySelector('#verification-error');
      const firstFocusableElement = modal.querySelectorAll(focusableElements)[0]
      firstFocusableElement.focus()
    }, 200);
  }

  private processFeatureSetFields(fields: any[]): void {
    for (let i = fields.length - 1; i >= 0; i--) {
      const field = fields[i];
      if ((field.fieldType === 'email' || field.fieldType === 'phone') && !this.aboutService.idUploadFlow) {
        const newField = new ConfirmFeatureSetField(field);
        fields.splice(i + 1, 0, newField);
      }

      if (field.fieldType === 'day' || field.fieldType === 'month' || field.fieldType === 'year') {
        const dateField = fields.find((f) => f instanceof DateFeatureSetField);
        if (dateField) {
          (dateField as DateFeatureSetField).dateParts.add(field.fieldType);
          this.dateField = dateField as DateFeatureSetField;
          const removed = fields.splice(i, 1);
          this.removedDateFields = this.removedDateFields.concat(removed);
        } else {
          const newField = new DateFeatureSetField(field, new Set([field.fieldType]));
          this.dateField = newField;
          this.removedDateFields = fields.splice(i, 1, newField);
        }
      }
    }
  }

  private addFormValidators(): void {
    for (let field of this.fields) {
      let validators: ValidatorFn[] = [];
      if (field.isMandatory && field.fieldType != 'date') {
        validators.push(Validators.required);
      }
      if (field.isPin) {
        validators.push(Validators.maxLength(this.maxPINLength));
        validators.push(Validators.pattern('[a-zA-Z0-9]*'));
      }
      if (field.fieldType === 'email') {
        validators.push(Validators.maxLength(this.maxEmailLength));
        validators.push(email());
      }
      if ((field instanceof ConfirmFeatureSetField) && (field.fieldType === 'confirm_email')) {
        validators.push(email());
        validators.push(new CustomValidators(field.confirmingField).confirmEqual);
      }
      if ((field instanceof ConfirmFeatureSetField) && field.fieldType === 'confirm_phone') {
        validators.push(new CustomValidators(field.confirmingField).confirmPhoneMatch);
      }
      if (field.fieldType != 'date') {
        this.verificationForm.addControl(field.fieldName, this.fb.control(null, validators));
      }
    }
    // One more pass to cross-check the confirming fields.
    for (let field of this.fields) {
      if (field.fieldType === 'email') {
        const confirmEmailField = this.fields.find(f => 
          f instanceof ConfirmFeatureSetField && 
          f.fieldType === 'confirm_email'
        );
        if (confirmEmailField) {
          this.verificationForm.get(field.fieldName)?.valueChanges.subscribe(() => {
            this.verificationForm.get(confirmEmailField.fieldName)?.updateValueAndValidity();
          });
        }
      }
      if (field.fieldType === 'phone') {
        const confirmPhoneField = this.fields.find(f => 
          f instanceof ConfirmFeatureSetField && 
          f.fieldType === 'confirm_phone'
        );
        if (confirmPhoneField) {
          this.verificationForm.get(field.fieldName)?.valueChanges.subscribe(() => {
            this.verificationForm.get(confirmPhoneField.fieldName)?.updateValueAndValidity();
          });
        }
      }
    }
  }

  proccessOrgVerifyRemainingAttempts() {
    this.userService.dereaseOrgVerifyAttemptsRemaining();
    if(this.userService.orgVerifyAttemptsRemaining === 1){
      this.dialogMessage += $localize`<br><br>You have 1 attempt remaining.`;
    } else if (this.userService.orgVerifyAttemptsRemaining === 0){
      this.dialogMessage += $localize`<br><br>You have no attempts remaining.<br><br>You may attempt to verify with an organization again in 15 minutes.`;
      this.userService.startOrgVerifyLockoutTimer();
      setTimeout(()=>{
        this.router.navigate(dashboardRoute);
      }, 3000);
    } else {
      this.dialogMessage += $localize`<br><br>You have ${this.userService.orgVerifyAttemptsRemaining} attempts remaining.`;
    }
  }

  // Trim text pasted into an input field.
  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
  
    const trimmedText = event.clipboardData?.getData('text').trim();
    const target = event.target as HTMLInputElement;
    const controlName = target.getAttribute('id');
    
    if (controlName) {
      this.verificationForm.get(controlName)?.setValue(trimmedText);
    }
  }


}

